import { cva } from 'class-variance-authority';
import * as React from 'react';
import type { NotificationBadgeProps } from './types';
import { cn } from '@utils';

const DEFAULT_MAX_COUNT = 99;

const badgeVariants = cva(
	'inline-flex items-center justify-center rounded-full font-semibold tabular-nums transition-all',
	{
		variants: {
			variant: {
				success: 'bg-success text-white',
				warning: 'bg-warning text-white',
				error: 'bg-error text-white',
				info: 'bg-info text-white',
				neutral: 'bg-secondary text-white',
			},
			size: {
				sm: 'min-w-4 h-4 text-[10px] px-1',
				md: 'min-w-5 h-5 text-xs px-1.5',
				lg: 'min-w-6 h-6 text-sm px-2',
			},
		},
		defaultVariants: {
			variant: 'success',
			size: 'md',
		},
	}
);

/**
 * NotificationBadge - A small count indicator badge.
 *
 * Used to show the number of notifications, unread items, or other counts.
 *
 * @example
 * ```tsx
 * <NotificationBadge count={5} variant="success" />
 * <NotificationBadge count={100} maxCount={99} /> // Shows "99+"
 * ```
 */
export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
	count,
	variant = 'success',
	size = 'md',
	showZero = false,
	maxCount = DEFAULT_MAX_COUNT,
	className,
	onClick,
	ariaLabel,
	testId = 'notification-badge',
}) => {
	if (count === 0 && !showZero) {
		return null;
	}

	const displayCount = count > maxCount ? `${maxCount}+` : count.toString();
	const badgeClassName = cn(
		badgeVariants({ variant, size }),
		onClick && 'cursor-pointer',
		className
	);

	if (onClick) {
		return (
			<button
				type="button"
				className={badgeClassName}
				onClick={onClick}
				aria-label={ariaLabel}
				data-testid={testId}
			>
				{displayCount}
			</button>
		);
	}

	return (
		<span className={badgeClassName} data-testid={testId}>
			{displayCount}
		</span>
	);
};

NotificationBadge.displayName = 'NotificationBadge';
